/**
 * Convierte una fecha/hora "local" en una timezone IANA al epoch UTC (segundos).
 * Usado en bulk insert: el usuario elige fecha/hora en el picker (que muestra hora del navegador);
 * interpretamos esos números como si fueran en la timezone seleccionada (ej. Argentina).
 *
 * Ej: usuario en España elige 20/3/2026 12:09 y timezone Argentina →
 *     debemos enviar 12:09 Argentina = 15:09 UTC, no 12:09 España = 11:09 UTC.
 */
function getOffsetHoursAtDate(utcDate: Date, timeZone: string): number {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  });
  const parts = formatter.formatToParts(utcDate);
  const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10);
  const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10);
  const second = parseInt(parts.find((p) => p.type === 'second')?.value ?? '0', 10);
  const localMs = (hour * 3600 + minute * 60 + second) * 1000;
  const utcMs =
    utcDate.getUTCHours() * 3600000 +
    utcDate.getUTCMinutes() * 60000 +
    utcDate.getUTCSeconds() * 1000;
  return (localMs - utcMs) / (3600 * 1000);
}

/**
 * Dado (año, mes 0-11, día, hora, minuto, segundo) interpretados como hora local
 * en la timezone dada, devuelve el epoch UTC en segundos.
 */
export function localTimeInTimezoneToUtcEpochSeconds(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string
): number {
  const refUtc = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
  const offsetHours = getOffsetHoursAtDate(refUtc, timeZone);
  const localAsUtc = new Date(Date.UTC(year, month, day, hour, minute, second, 0));
  const utcDate = new Date(localAsUtc.getTime() - offsetHours * 3600 * 1000);
  return Math.floor(utcDate.getTime() / 1000);
}

/**
 * Dado un Date (típicamente del date picker, en hora local del navegador),
 * interpreta sus componentes de fecha/hora como si fueran en la timezone dada
 * y devuelve el epoch UTC en segundos.
 * Así, si el usuario en España elige 12:09 y la TZ del formulario es Argentina,
 * devolvemos el instante 12:09 en Argentina (no 12:09 en España).
 */
export function datePickerValueAsTimezoneToUtcEpochSeconds(date: Date, timeZone: string): number {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hour = date.getHours();
  const minute = date.getMinutes();
  const second = date.getSeconds();
  return localTimeInTimezoneToUtcEpochSeconds(year, month, day, hour, minute, second, timeZone);
}
